import { useEffect, useMemo } from 'react'

import { useEditor } from '@craftjs/core'
import { ListItem, Separator, ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger, DefaultTooltip } from 'twyne-new.ui'

import TextTruncated from '@/components/TextTruncated'
import TemplateBlock from '@/components/builder/blocks/TemplateBlock'
import { useParseSerialized } from '@/components/builder/utils/serialize-block'

import { useBuilderStore } from '../../../store'

import Virtualizer from './Virtualizer'
import { useBlocks } from './hooks'

export default function Content({ active, value, ...props }) {
  const { pagination: { reset } } = useBlocks()

  useEffect(() => {
    reset()
  }, [value, reset])

  if (!active) {
    return null;
  }

  return <BlockList {...props} />
}

function BlockList({ params: queryParams }) {
  const { blocks: { useLoad, items: rows, hasNextPage, reset } } = useBuilderStore()
  const { pagination: { options, onNextPage }, filters: { term } } = useBlocks()

  const params = useMemo(() => ({
    options,
    term,
    autoRefresh: false,
    ...queryParams || {},
  }), [options, term, queryParams])

  useLoad(params)

  useEffect(() => () => reset(), [reset])

  return <Virtualizer rows={rows} total={rows?.length} hasMore={hasNextPage} onVisit={onNextPage}>
    <BlockItem />
  </Virtualizer>
}

function BlockItem({ row }) {
  const { connectors: { create }, actions: { addNodeTree, setCustom } } = useEditor()
  const { templateBlock: { fetchBlockId }, block: { deleteEntry }, blocks: { reset } } = useBuilderStore()
  const parse = useParseSerialized()

  const { Component } = TemplateBlock
  const { id, name, meta } = row
  const { url } = meta?.file || {}

  const handleLoad = async () => {
    const cmsBuilderBlock = await fetchBlockId(id)
    if (cmsBuilderBlock) {
      const { definition } = cmsBuilderBlock
      return definition
    }
  }

  const handleCreate = ref => create(ref, <Component />, {
    onCreate: async ({ rootNodeId }) => {
      const definition = await handleLoad()
      if (definition) {
        addNodeTree(parse(definition), rootNodeId, 1)
        setCustom(rootNodeId, custom => custom.blockId = id)
      }
    },
  })

  const handleDelete = async () => {
    await deleteEntry({ id })
    reset()
  }

  return <div ref={handleCreate}>
    <ListItem
      className='flex flex-col gap-1 justify-center p-0 mx-2 my-0 mt-2 twy-bg-base-accent rounded-sm'
      value={<DefaultTooltip contentProps={{ className: 'bg-white shadow-sm' }} arrowProps={{ className: 'fill-white stroke-gray-300' }}
        content={<img src={url} className='w-full object-fit rounded-xs bg-gray-100 border border-gray-200' />} side='right'>
        <ContextAction onDelete={handleDelete}>
          <div className='p-2'>
            <img src={url} className='w-full object-fit rounded-xs bg-gray-100 shadow-sm' />
            <Separator orientation='horizontal' className='m-0 mt-2 mb-1' />
            <TextTruncated className='text-left' maxLength={25}>{name}</TextTruncated>
          </div>
        </ContextAction>
      </DefaultTooltip>}
    />
  </div>
}

function ContextAction({ children, onDelete }) {
  return <ContextMenu>
    <ContextMenuTrigger>
      {children}
    </ContextMenuTrigger>
    <ContextMenuContent>
      <ContextMenuItem onClick={onDelete}>
        Delete
      </ContextMenuItem>
    </ContextMenuContent>
  </ContextMenu>
}
